import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import { authService } from '../../services/auth/authService';
import { patientService } from '../../services/patients/patientService';
import './CreatePatientPage.css';

// Step components
import BasicDetailsStep, { BasicDetailsStepRef } from './onboarding/BasicDetailsStep';
import HealthConditionsStep from './onboarding/HealthConditionsStep';
import LifestyleStep from './onboarding/LifestyleStep';
// Nutrition profile capture (meal recall, preferences, constraints, patterns) is handled
// during plan creation, not during patient account creation.

interface OnboardingData {
  basic: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    date_of_birth: string;
    gender: 'male' | 'female' | 'other' | '';
    height_cm?: number;
    weight_kg?: number;
    body_type?: 'ectomorph' | 'mesomorph' | 'endomorph';
    activity_level?: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active' | 'super_active';
    timezone: string;
    timezone_id?: number;
    email: string;
    phone_number: string;
    country_code_id?: number;
  };
  conditions: {
    condition_names: string[];
    other_condition_text?: string;
    allergies: string[];
    other_allergy_text?: string;
  };
  lifestyle: {
    smokes: boolean;
    drinks_alcohol: boolean;
    exercises_regularly: boolean;
    exercise_type?: 'gym' | 'running' | 'yoga' | 'other';
    exercise_frequency_per_week?: number;
  };
  notifications: {
    timezone: string;
    window_start_local: string;
    window_end_local: string;
    email_enabled: boolean;
    sms_enabled: boolean;
    push_enabled: boolean;
  };
}

const CreatePatientPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const basicDetailsStepRef = React.useRef<BasicDetailsStepRef>(null);
  /** Step 4: when false, do not send welcome/verification email; doctor can send later after setting up nutrition profile and meal plan */
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState<boolean>(true);
  /** When patient created without email, show large success modal with next steps */
  const [successModalNoEmail, setSuccessModalNoEmail] = useState<{ patientId: number } | null>(null);

  const [formData, setFormData] = useState<Partial<OnboardingData>>({
    basic: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      email: '', // No default email - must be entered by user
      phone_number: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    conditions: {
      condition_names: [],
      allergies: [],
    },
    lifestyle: {
      smokes: false,
      drinks_alcohol: false,
      exercises_regularly: false,
    },
    notifications: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      window_start_local: '09:00',
      window_end_local: '21:00',
      email_enabled: true,
      sms_enabled: false,
      push_enabled: true,
    },
  });

  const steps = [
    { id: 'basic', title: 'Basic Details', component: BasicDetailsStep },
    { id: 'conditions', title: 'Health Details', component: HealthConditionsStep },
    { id: 'lifestyle', title: 'Lifestyle', component: LifestyleStep },
    { id: 'notifications', title: 'Notification preferences', component: null },
    { id: 'notify', title: 'Notify patient', component: null }, // Step 5: optional send email; rendered inline
  ];

  const handleNext = (stepData: any) => {
    const stepId = steps[currentStep].id;
    setFormData((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId as keyof OnboardingData], ...stepData },
    }));

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Normalize optional fields (backend rejects empty-string for optional enums/ints)
      const normalizeOptionalNumber = (v: any): number | undefined => {
        if (v === null || v === undefined) return undefined;
        if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
        if (typeof v === 'string') {
          const trimmed = v.trim();
          if (!trimmed) return undefined;
          const n = Number(trimmed);
          return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
      };
      const normalizeOptionalString = (v: any): string | undefined => {
        if (v === null || v === undefined) return undefined;
        if (typeof v !== 'string') return undefined;
        const trimmed = v.trim();
        return trimmed ? trimmed : undefined;
      };

      const basic = {
        ...formData.basic!,
        body_type: normalizeOptionalString((formData.basic as any)?.body_type) as any,
        activity_level: normalizeOptionalString((formData.basic as any)?.activity_level) as any,
        timezone_id: normalizeOptionalNumber((formData.basic as any)?.timezone_id),
        country_code_id: normalizeOptionalNumber((formData.basic as any)?.country_code_id),
      };

      const lifestyle = {
        ...formData.lifestyle!,
        // iOS sends nil when empty; backend Optional[Literal] rejects ""
        exercise_type: normalizeOptionalString((formData.lifestyle as any)?.exercise_type) as any,
        // Keep frequency numeric; iOS sends 0 even when not exercising
        exercise_frequency_per_week: normalizeOptionalNumber((formData.lifestyle as any)?.exercise_frequency_per_week),
      };

      // Notification preferences: use step data if set, else defaults (same shape as patient onboarding)
      const notifications = formData.notifications ?? {
        timezone: basic.timezone,
        window_start_local: '09:00',
        window_end_local: '21:00',
        email_enabled: true,
        sms_enabled: false,
        push_enabled: true,
      };

      const onboardingPayload = {
        basic,
        conditions: formData.conditions!,
        lifestyle,
        notifications: {
          ...notifications,
          timezone: notifications.timezone || basic.timezone,
        },
        consents: [], // Empty - patient will provide consents when they log in
      };

      // Create patient account via doctor endpoint (this handles both user creation and onboarding)
      // Validation in Basic Details step ensures gender is set before proceeding; cast satisfies API type.
      const userData = await patientService.createPatientAccount(
        formData.basic!.email,
        null, // Let backend generate temporary password
        onboardingPayload as Parameters<typeof patientService.createPatientAccount>[2],
        sendWelcomeEmail
      );

      if (sendWelcomeEmail) {
        alert(`Patient created successfully!\n\nA verification email with the temporary password has been sent to ${formData.basic!.email}.\n\nThe patient can:\n- Use the temporary password to sign in\n- Use Google Sign-In to access their account\n- Change the password after first login`);
      } else {
        setSuccessModalNoEmail({ patientId: (userData as any).id });
        return;
      }

      // Navigate back to patients list
      navigate('/patients');
    } catch (err: any) {
      console.error('Failed to create patient:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      // FastAPI 422 errors come as array of {loc, msg, type}. Format them nicely.
      const prettyFromArray = (items: any[]): string | null => {
        if (!Array.isArray(items)) return null;
        const humanize = (locParts: any[]): string => {
          if (!Array.isArray(locParts)) return '';
          const filtered = locParts.filter(
            (p) => typeof p === 'string' && p !== 'body' && p !== 'onboarding_data'
          );
          const joined = filtered.join(' > ').replace(/_/g, ' ');
          return joined.charAt(0).toUpperCase() + joined.slice(1);
        };
        const parts = items.map((d) => {
          const loc = humanize(d?.loc);
          const msg = d?.msg || d?.detail || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        });
        return parts.join('; ');
      };

      const prettyDetail =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? prettyFromArray(detail)
            : detail
              ? JSON.stringify(detail)
              : null;

      const fallback =
        status === 422
          ? 'Some required details are missing. Please fill the highlighted fields in earlier steps and try again.'
          : 'Something went wrong while creating the patient. Please try again.';

      setError(prettyDetail || fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const handleCancel = () => {
    // Navigate back to patients list without saving
    navigate('/patients');
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      {successModalNoEmail && (
        <div className="create-patient-success-modal-overlay" onClick={() => { setSuccessModalNoEmail(null); navigate(`/patients/${successModalNoEmail.patientId}`); }}>
          <div className="create-patient-success-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="create-patient-success-modal-title">Patient created successfully!</h2>
            <p className="create-patient-success-modal-subtitle">No email was sent. Next steps:</p>
            <ul className="create-patient-success-modal-list">
              <li>Open this patient&apos;s profile and create their nutrition profile and meal plan.</li>
              <li>Set up appointments with the patient.</li>
              <li>When ready, use &quot;Send Reminder Email&quot; on the patient&apos;s row to send them the welcome email with login details.</li>
            </ul>
            <button
              type="button"
              className="btn-primary create-patient-success-modal-close"
              onClick={() => { setSuccessModalNoEmail(null); navigate(`/patients/${successModalNoEmail.patientId}`); }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-layout">
        <DashboardSidebar items={createSidebarItems(navigate, '/patients')} />

        <div className="dashboard-container">
          <main className="dashboard-main-content create-patient-content">
            <div className="onboarding-container">
              <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                <Link to="/patients" className="breadcrumb-link">Patients</Link>
                <span className="breadcrumb-sep" aria-hidden>›</span>
                <span className="breadcrumb-current">Create Patient</span>
              </nav>
              {/* Progress Bar */}
              <div className="onboarding-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="progress-text">
                  Step {currentStep + 1} of {steps.length}
                </div>
              </div>

              {/* Step Title */}
              <h1 className="onboarding-title">{steps[currentStep].title}</h1>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {/* Current Step Component */}
              <div className="step-content">
                {steps[currentStep].id === 'basic' ? (
                  <BasicDetailsStep
                    ref={basicDetailsStepRef}
                    data={formData[steps[currentStep].id as keyof OnboardingData]}
                    onChange={(data: any) => {
                      setFormData((prev) => ({
                        ...prev,
                        [steps[currentStep].id]: { ...prev[steps[currentStep].id as keyof OnboardingData], ...data },
                      }));
                    }}
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                ) : steps[currentStep].id === 'notify' ? (
                  <div className="notify-step">
                    <p className="notify-step-intro">Choose whether to send the patient a welcome email now or after you set up their nutrition profile and meal plan.</p>
                    <label className="notify-option">
                      <input
                        type="radio"
                        name="sendWelcomeEmail"
                        checked={sendWelcomeEmail === true}
                        onChange={() => setSendWelcomeEmail(true)}
                      />
                      <span>Send welcome email now – verification link and temporary password will be sent to the patient.</span>
                    </label>
                    <label className="notify-option">
                      <input
                        type="radio"
                        name="sendWelcomeEmail"
                        checked={sendWelcomeEmail === false}
                        onChange={() => setSendWelcomeEmail(false)}
                      />
                      <span>Don&apos;t send email yet – I&apos;ll set up nutrition profile and meal plan first, then send the email from the patient&apos;s page.</span>
                    </label>
                  </div>
                ) : steps[currentStep].id === 'notifications' ? (
                  <div className="notify-step notifications-step">
                    <p className="notify-step-intro">Set how the patient can receive reminders and messages. These match the options patients see in the app and can be changed by them later.</p>
                    <label className="notify-option notify-toggle">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.email_enabled ?? true}
                        onChange={(e) => setFormData((prev) => {
                          const base = prev.notifications ?? {
                            timezone: prev.basic?.timezone ?? '',
                            window_start_local: '09:00',
                            window_end_local: '21:00',
                            email_enabled: true,
                            sms_enabled: false,
                            push_enabled: true,
                          };
                          return { ...prev, notifications: { ...base, email_enabled: e.target.checked } };
                        })}
                      />
                      <span>Email notifications</span>
                    </label>
                    <label className="notify-option notify-toggle">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.sms_enabled ?? false}
                        onChange={(e) => setFormData((prev) => {
                          const base = prev.notifications ?? {
                            timezone: prev.basic?.timezone ?? '',
                            window_start_local: '09:00',
                            window_end_local: '21:00',
                            email_enabled: true,
                            sms_enabled: false,
                            push_enabled: true,
                          };
                          return { ...prev, notifications: { ...base, sms_enabled: e.target.checked } };
                        })}
                      />
                      <span>SMS / WhatsApp notifications</span>
                    </label>
                    <label className="notify-option notify-toggle">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.push_enabled ?? true}
                        onChange={(e) => setFormData((prev) => {
                          const base = prev.notifications ?? {
                            timezone: prev.basic?.timezone ?? '',
                            window_start_local: '09:00',
                            window_end_local: '21:00',
                            email_enabled: true,
                            sms_enabled: false,
                            push_enabled: true,
                          };
                          return { ...prev, notifications: { ...base, push_enabled: e.target.checked } };
                        })}
                      />
                      <span>Push notifications (in-app)</span>
                    </label>
                  </div>
                ) : steps[currentStep].id === 'conditions' ? (
                  <HealthConditionsStep
                    data={formData[steps[currentStep].id as keyof OnboardingData]}
                    onChange={(data: any) => {
                      setFormData((prev) => ({
                        ...prev,
                        [steps[currentStep].id]: { ...prev[steps[currentStep].id as keyof OnboardingData], ...data },
                      }));
                    }}
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                ) : (
                  <LifestyleStep
                    data={formData[steps[currentStep].id as keyof OnboardingData]}
                    onChange={(data: any) => {
                      setFormData((prev) => ({
                        ...prev,
                        [steps[currentStep].id]: { ...prev[steps[currentStep].id as keyof OnboardingData], ...data },
                      }));
                    }}
                    onNext={handleNext}
                    onBack={handleBack}
                  />
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="onboarding-actions">
                <div className="actions-left">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleBack}
                      disabled={loading}
                    >
                      ← Back
                    </button>
                  )}
                </div>
                <div className="actions-right">
                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        // For basic step, trigger validation via ref
                        if (steps[currentStep].id === 'basic' && basicDetailsStepRef.current) {
                          basicDetailsStepRef.current.validateAndProceed();
                        } else {
                          // For other steps, proceed directly
                          const stepData = formData[steps[currentStep].id as keyof OnboardingData];
                          handleNext(stepData);
                        }
                      }}
                      disabled={loading}
                    >
                      Next →
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Patient'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default CreatePatientPage;
